import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMall } from "@/context/MallContext";
import { useEnterShop } from "@/lib/useShop";
import { btnChip, btnPrimary, input, money } from "@/components/simple/product/Fields";

const PRESETS = [10, 50, 100];

/** `/pay` (alias `/artwork/pay`) — pay any amount (7.11): $10 / $50 / $100 / Other → Stripe, paying the current shop. */
export default function Pay() {
    const [sp, setSp] = useSearchParams();
    const { pathname } = useLocation();
    const { user } = useAuth();
    const mall = useMall();
    const artwork = pathname.startsWith("/artwork");
    const qAmount = Number(sp.get("amount"));
    const [amount, setAmount] = useState(qAmount > 0 ? qAmount : 0);
    const [other, setOther] = useState(qAmount > 0 && !PRESETS.includes(qAmount) ? String(qAmount) : "");
    const [form, setForm] = useState({
        name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
        purpose: sp.get("for") || (artwork ? "Artwork" : ""), order_number: sp.get("order") || "",
    });
    const [ctx, setCtx] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [cancelled, setCancelled] = useState(false);
    const storeSlug = sp.get("store") || mall.store?.slug || undefined;
    useEnterShop(ctx?.shop?.slug && ctx.shop.slug !== "13th-ave-sign-and-shirt" ? ctx.shop.slug : null);

    useEffect(() => {
        const id = sp.get("cancelled");
        if (!id) return;
        api.post(`/orders/${id}/abandon`).catch(() => { });
        setCancelled(true);
        setSp((p) => { p.delete("cancelled"); return p; }, { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        api.get("/pay/context", { params: { store: storeSlug, order: form.order_number.trim() || undefined } })
            .then(({ data }) => {
                setCtx(data);
                if (data.order?.balance_due > 0 && !qAmount) { setAmount(data.order.balance_due); setOther(PRESETS.includes(data.order.balance_due) ? "" : String(data.order.balance_due)); }
                if (data.order?.balance_for && !form.purpose) setForm((f) => ({ ...f, purpose: data.order.balance_for }));
            }).catch(() => setCtx({ shop: { name: "13thav", accepts_cards: true }, min: 1, max: 5000 }));
    }, [storeSlug, form.order_number]); // eslint-disable-line react-hooks/exhaustive-deps

    const min = ctx?.min ?? 1, max = ctx?.max ?? 5000;
    const valid = amount >= min && amount <= max;
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const pickOther = (v) => { setOther(v); const n = Number(v); setAmount(n > 0 ? n : 0); };
    const shopName = useMemo(() => ctx?.shop?.name || "the shop", [ctx]);

    const pay = async (e) => {
        e.preventDefault();
        if (!valid) { setError(`Enter an amount between ${money(min)} and ${money(max.toLocaleString())}.`); return; }
        setBusy(true); setError("");
        try {
            const { data } = await api.post("/pay", {
                amount, name: form.name, phone: form.phone, email: form.email, purpose: form.purpose,
                order_number: form.order_number, store_slug: ctx?.pay_slug || storeSlug, origin_url: window.location.origin,
                kind: artwork ? "artwork_payment" : "payment",
            });
            sessionStorage.setItem("pending_pay", JSON.stringify({ slug: null, order_id: data.order_id }));
            window.location.href = data.checkout_url;
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || "Couldn't open the register. Try again.");
            setBusy(false);
        }
    };

    return (
        <Layout title={artwork ? "Pay for artwork" : "Pay a balance"}>
            <form onSubmit={pay} className="mx-auto w-full max-w-4xl px-3 py-4 text-ink sm:px-6" data-testid="pay-page">
                <h1 className="text-[28px] font-semibold leading-tight">{artwork ? "Pay for artwork" : "Pay a balance"}</h1>
                <p className="mt-1 text-[16px] text-muted-ink" data-testid="pay-shop">Paying {shopName}.</p>
                {cancelled && <p className="mt-2 border border-ink px-3 py-2 text-[16px]" data-testid="pay-cancelled-line">No charge was made.</p>}
                {ctx?.order && (
                    <p className="mt-2 text-[16px]" data-testid="pay-order-line">
                        Order {ctx.order.order_number}{ctx.order.balance_due > 0 ? ` · balance due ${money(ctx.order.balance_due)}${ctx.order.balance_for ? ` for ${ctx.order.balance_for}` : ""}` : ""}
                    </p>
                )}
                <div className="mt-4 grid grid-cols-4 gap-2" data-testid="pay-presets">
                    {PRESETS.map((p) => (
                        <button key={p} type="button" onClick={() => { setAmount(p); setOther(""); }} className={btnChip(amount === p && other === "")} data-testid={`pay-preset-${p}`}>${p}</button>
                    ))}
                    <input type="number" inputMode="decimal" min={min} max={max} step="0.01" placeholder="Other" value={other} onChange={(e) => pickOther(e.target.value)}
                        aria-label="Other amount" className={input} data-testid="pay-other" />
                </div>
                <div className="mt-4 flex flex-col gap-3">
                    <input className={input} placeholder="Name" value={form.name} onChange={set("name")} required autoComplete="name" data-testid="pay-name" />
                    <input className={input} type="tel" placeholder="Mobile phone" value={form.phone} onChange={set("phone")} required autoComplete="tel" data-testid="pay-phone" />
                    <input className={input} type="email" placeholder="Email" value={form.email} onChange={set("email")} required autoComplete="email" data-testid="pay-email" />
                    <input className={input} placeholder="What is this for? (optional)" value={form.purpose} onChange={set("purpose")} data-testid="pay-purpose" />
                    <input className={input} placeholder="Order # (optional)" value={form.order_number} onChange={set("order_number")} data-testid="pay-order" />
                </div>
                {error && <p className="mt-3 text-[16px]" style={{ color: "var(--error)" }} data-testid="pay-error">{error}</p>}
                <button type="submit" disabled={busy || !valid || ctx?.shop?.accepts_cards === false} className={`${btnPrimary} mt-4`} data-testid="pay-submit">
                    {busy ? "Working…" : valid ? `Pay ${money(amount)}` : "Pay"}
                </button>
                {ctx?.shop?.accepts_cards === false && <p className="mt-2 text-[16px]" data-testid="pay-no-register">{shopName} can't take cards yet{ctx.shop.phone ? ` — text ${ctx.shop.phone}` : ""}.</p>}
                <Link to="/" className="mt-3 flex min-h-[44px] items-center text-[16px] underline" data-testid="pay-home">Home</Link>
            </form>
        </Layout>
    );
}
